import { useMemo } from 'react';

import { createSelector } from '@reduxjs/toolkit';
import {
  TypedUseQueryHookResult,
  skipToken,
} from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '~/api/axiosBaseQuery';
import {
  StoreQueryParams,
  ZipcodeLocationResponse,
  useFetchStoresQuery,
  useFetchZipcodeLocationQuery,
} from '~/api/easy-estimator-api/stores-api/storesService';
import { shouldFetchFn } from '~/api/utils';
import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import { StoreModel } from '~/core/models/store.model';
import { useAppSelector } from '~/redux/hooks';
import { selectZipcode } from '~/redux/slices/zipcode';

const emptyArray = [] satisfies StoreModel[];

type useStoresReturn = Omit<
  TypedUseQueryHookResult<
    StoreModel[],
    StoreQueryParams,
    AxiosBaseQueryFunction
  >,
  'data' | 'isFetching' | 'isError'
> & {
  stores: StoreModel[];
  zipCodeLocation?: ZipcodeLocationResponse;
  isFetchingStores: boolean;
  isErrorStores: boolean;
};

export const useStores = (): useStoresReturn => {
  const sortByDistance = useMemo(
    () =>
      createSelector(
        [(data?: StoreModel[]) => data],
        (data) =>
          data
            ?.map((store) => ({
              ...store,
              distance:
                Math.round(store.distance * Math.pow(10, 1)) / Math.pow(10, 1),
            }))
            ?.sort((a, b) => a.distance - b.distance) ?? emptyArray
      ),
    []
  );

  const zipCode = useAppSelector(selectZipcode);
  const { fetchedEstimate } = useFetchedEstimate();
  const shouldFetch = shouldFetchFn({ args: [zipCode] });

  const { stores = emptyArray, ...storeResp } = useFetchStoresQuery(
    shouldFetch
      ? { params: { zipCode, storeId: fetchedEstimate?.storeId } }
      : skipToken,
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        stores: sortByDistance(data),
      }),
    }
  );

  const { zipCodeLocation, ...locationResp } = useFetchZipcodeLocationQuery(
    zipCode ? zipCode : skipToken,
    {
      selectFromResult: (result) => ({
        zipCodeLocation: result.isError ? undefined : result.data,
        ...result,
      }),
    }
  );

  return {
    stores: zipCode ? stores : [],
    zipCodeLocation,
    isFetchingStores: storeResp.isFetching || locationResp.isFetching,
    isErrorStores: storeResp.isError || locationResp.isError,
    ...storeResp,
  };
};
